/* eslint-disable no-console */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { CUENTAS_ER, getVidaUtilPorTipo } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { getEdificiosOperativos, useCurrency } from "../../utils";
import { crearActivo } from "../../utils/activos";
import { getDolarValue, getUFValue } from "../../utils/exchangeRateService";
import { getFacturasPendientesCobro } from "../../utils/facturasPendientesCobro";
// NOTA: FlujoCaja V1 está deprecado y desrouteado (/admin/flujo-caja renderiza FlujoCajaV2).
// El import de ../../utils/facturasProveedor se eliminó al retirar la tabla facturas_proveedor;
// los usos de getFacturasProveedor/crear/actualizar/uploadArchivoFactura abajo quedan como
// referencias muertas en este componente que ya no se monta. No migrar V1 — se retirará entero.
import {
  getMovimientosCaja,
  crearMovimiento,
  crearMovimientoConHijo,
  actualizarMovimiento,
  anularMovimiento,
  anularMovimientoConHijos,
} from "../../utils/flujoCaja";
import { getProveedores, crearProveedor, actualizarProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import { updateContrato } from "../../utils/updateContrato";
import { updateOportunidad } from "../../utils/updateOportunidad";
import { updateOportunidadBTL } from "../../utils/updateOportunidadBTL";
import { Header } from "../Header";
import CambioMonedaModal from "./CambioMonedaModal";
import EstadoCuentaTab from "./EstadoCuentaTab";
import FacturasProveedorTab from "./FacturasProveedorTab";
import MovimientoModal from "./MovimientoModal";
import { CATEGORIAS_LABEL } from "./ResumenSaldo";
import styles from "./FlujoCaja.module.scss";

// =====================================================
// HELPERS
// =====================================================

const formatFechaDisplay = (fechaStr) => {
  if (!fechaStr) return "-";
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

const CUENTA_ER_LABEL = {};
CUENTAS_ER.forEach((c) => {
  CUENTA_ER_LABEL[c.value] = c.label;
});

const FACTURACION_TIPO_LABEL = {
  pauta: "Publicidad",
  BTL: "BTL",
  Contratos: "Gestión",
  contrato: "Gestión",
  "HW-CAPEX": "Hardware",
  "HW-OPEX": "Hardware",
  Gestión: "Gestión",
  combined: "Hardware + Gestión",
};

const getCategoriaCobroLabel = (facturacionTipo) => {
  if (!facturacionTipo) return "Cobro";
  return FACTURACION_TIPO_LABEL[facturacionTipo] || facturacionTipo;
};

const monedaSimbolo = (moneda) => {
  if (moneda === "USD") return "US$";
  if (moneda === "PEN") return "S/";
  return "$";
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const FlujoCaja = () => {
  const { pais, id } = useAuthContext();
  const canEdit = [1, 4, 7, 9].includes(id);
  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : null;
  const { formatAmount: formatAmountBase } = useCurrency();

  // Wrapper local: CLP/PEN explícitos, UF se convierte a CLP
  const formatAmount = useCallback(
    (amount, moneda, customOptions) => {
      if (moneda === "UF") return formatAmountBase(amount, "UF", customOptions);
      const result = formatAmountBase(amount, moneda, customOptions);
      if (moneda === "CLP") return result.replace("$ ", "CLP ");
      if (moneda === "PEN") return result.replace("S/ ", "PEN ");
      return result;
    },
    [formatAmountBase]
  );

  // Estado principal
  const [movimientos, setMovimientos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedoresState] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [proyectosCarteleria, setProyectosCarteleria] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingMovimiento, setEditingMovimiento] = useState(null);
  const [defaultTipo, setDefaultTipo] = useState("cobro");
  const [showCambioModal, setShowCambioModal] = useState(false);

  // Tab activo
  const [activeTab, setActiveTab] = useState("movimientos");
  const [autoOpenFacturaModal, setAutoOpenFacturaModal] = useState(false);

  // Filtros
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroMoneda, setFiltroMoneda] = useState("");
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("2026-03-01");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [mostrarDecimales, setMostrarDecimales] = useState(false);
  const [mostrarPrevios, setMostrarPrevios] = useState(false);
  const [filtroOrigen, setFiltroOrigen] = useState("");
  const [pendientesBancarios, setPendientesBancarios] = useState(0);
  const [siiRecibidasVinculadas, setSiiRecibidasVinculadas] = useState([]);

  // Formato sin prefijo de moneda (solo el número)
  const formatNum = useCallback(
    (amount) => {
      const num = Number(amount) || 0;
      const locale = normalizedPais === "Chile" ? "es-CL" : "es-PE";
      const decimals = mostrarDecimales ? 2 : 0;
      return num.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    },
    [normalizedPais, mostrarDecimales]
  );

  // Confirmar anulación
  const [confirmDelete, setConfirmDelete] = useState(null);

  // =====================================================
  // FETCH DATA
  // =====================================================

  const fetchData = async () => {
    if (!normalizedPais) return;
    setLoading(true);
    try {
      const monedaLocal = normalizedPais === "Chile" ? "CLP" : "PEN";
      const [movData, clientesData, provData, empData, edifData, factData, cartData, cobroData] = await Promise.all([
        getMovimientosCaja(normalizedPais),
        client
          .from("clientes")
          .select("id, nombre")
          .eq("pais", normalizedPais)
          .order("nombre", { ascending: true })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          }),
        getProveedores(normalizedPais),
        client
          .from("empleados")
          .select("id, nombre, apellido, egreso_fecha")
          .eq("pais", normalizedPais)
          .order("nombre", { ascending: true })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          }),
        getEdificiosOperativos({ pais: normalizedPais }).catch(() => []),
        getFacturasProveedor(normalizedPais).catch((err) => {
          console.error("Error cargando facturas:", err);
          return [];
        }),
        client
          .from("negocios_carteleria")
          .select("id, nombre_proyecto, referencia_id")
          .eq("pais", normalizedPais)
          .order("nombre_proyecto", { ascending: true })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          })
          .catch(() => []),
        getFacturasPendientesCobro(normalizedPais, monedaLocal).catch(() => []),
      ]);

      setMovimientos(movData);
      setClientes(clientesData);
      setProveedoresState(provData);
      setEmpleados(empData);
      setEdificios(edifData);

      // Recalcular monto_pagado con lógica de cuenta corriente:
      // excedentes de una factura se arrastran a la siguiente del mismo proveedor+moneda
      const facturasEnriquecidas = [...factData];
      const convertirAMoneda = (monto, monedaOrigen, monedaDestino, fechaPago) => {
        if (monedaOrigen === monedaDestino) return monto;
        const monedaLocal = normalizedPais === "Chile" ? "CLP" : "PEN";
        const dolar = getDolarValue(fechaPago, normalizedPais);
        let enLocal = Number(monto);
        if (monedaOrigen === "USD") enLocal = monto * dolar;
        else if (monedaOrigen === "UF") enLocal = monto * getUFValue(fechaPago);
        if (monedaDestino === monedaLocal) return enLocal;
        if (monedaDestino === "USD") return dolar ? enLocal / dolar : 0;
        if (monedaDestino === "UF") return getUFValue(fechaPago) ? enLocal / getUFValue(fechaPago) : 0;
        return monto;
      };
      const provIds = [...new Set(factData.map((f) => f.proveedor_id))];
      for (const provId of provIds) {
        const factsDelProv = factData.filter((f) => f.proveedor_id === provId);
        const monedas = [...new Set(factsDelProv.map((f) => f.moneda))];
        for (const mon of monedas) {
          const factsEnMoneda = factsDelProv
            .filter((f) => f.moneda === mon)
            .sort((a, b) => (a.fecha_emision || "").localeCompare(b.fecha_emision || ""));
          let carryOver = 0;
          for (const f of factsEnMoneda) {
            const pagos = movData.filter(
              (m) => Number(m.facturacion_id) === f.id && m.estado !== "anulado" && m.tipo === "pago"
            );
            let pagado = carryOver;
            for (const p of pagos) {
              pagado += convertirAMoneda(Number(p.monto), p.moneda, mon, p.fecha);
            }
            const total = Number(f.monto_total);
            carryOver = pagado > total ? pagado - total : 0;
            const aplicado = Math.min(pagado, total);
            const decimales = mon === "USD" || mon === "UF";
            const redondeado = decimales ? Math.round(aplicado * 100) / 100 : Math.round(aplicado);
            const idx = facturasEnriquecidas.findIndex((fe) => fe.id === f.id);
            if (idx >= 0) facturasEnriquecidas[idx] = { ...facturasEnriquecidas[idx], monto_pagado: redondeado };
          }
        }
      }
      setFacturas(facturasEnriquecidas);
      setProyectosCarteleria(cartData);
      setPendientesCobro(cobroData);

      // Cargar facturas SII recibidas vinculadas (para indicador "Verificado SII")
      const { data: siiRecData } = await client
        .from("facturas_sii_recibidas")
        .select("folio, proveedor_id")
        .eq("pais", normalizedPais)
        .eq("estado_vinculacion", "vinculada");
      setSiiRecibidasVinculadas(siiRecData || []);

      // Contar movimientos bancarios pendientes
      const { count } = await client
        .from("movimientos_bancarios")
        .select("id", { count: "exact", head: true })
        .eq("pais", normalizedPais)
        .eq("estado", "pendiente");
      setPendientesBancarios(count || 0);
    } catch (err) {
      console.error("Error cargando datos flujo de caja:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedPais]);

  // =====================================================
  // FILTRADO
  // =====================================================

  const CORTE_MARZO = "2026-03-01";

  const movimientosFiltrados = useMemo(() => {
    let filtered = [...movimientos];
    if (filtroTipo) filtered = filtered.filter((m) => m.tipo === filtroTipo);
    if (filtroOrigen) filtered = filtered.filter((m) => (m.origen || "manual") === filtroOrigen);
    if (filtroMoneda) {
      const monedaLocal = normalizedPais === "Chile" ? "CLP" : "PEN";
      filtered =
        filtroMoneda === "local"
          ? filtered.filter((m) => m.moneda === monedaLocal || !m.moneda)
          : filtered.filter((m) => m.moneda === filtroMoneda);
    }
    if (mostrarPrevios) {
      // Modo histórico: mostrar solo movimientos anteriores a marzo 2026
      filtered = filtered.filter((m) => m.fecha < CORTE_MARZO);
    } else {
      // Modo actual: mostrar solo movimientos desde marzo 2026 en adelante
      filtered = filtered.filter((m) => m.fecha >= CORTE_MARZO);
      if (filtroFechaDesde && filtroFechaDesde > CORTE_MARZO)
        filtered = filtered.filter((m) => m.fecha >= filtroFechaDesde);
      if (filtroFechaHasta) filtered = filtered.filter((m) => m.fecha <= filtroFechaHasta);
    }

    // Para "Cambio de Moneda": incluir la pata faltante aunque no pase los filtros de tipo/moneda
    const filteredIds = new Set(filtered.map((m) => m.id));
    const isCambio = (m) => m.entidad_nombre === "Cambio de Moneda";
    for (const m of filtered) {
      if (!isCambio(m)) continue;
      if (m.movimiento_padre_id && !filteredIds.has(m.movimiento_padre_id)) {
        // Hijo presente, padre ausente → agregar padre
        const padre = movimientos.find((p) => p.id === m.movimiento_padre_id);
        if (padre) {
          filtered.push(padre);
          filteredIds.add(padre.id);
        }
      }
      if (!m.movimiento_padre_id) {
        // Padre presente → agregar hijos ausentes
        for (const hijo of movimientos) {
          if (hijo.movimiento_padre_id === m.id && !filteredIds.has(hijo.id)) {
            filtered.push(hijo);
            filteredIds.add(hijo.id);
          }
        }
      }
    }

    // Agrupar hijos inmediatamente después del padre
    const parents = filtered.filter((m) => !m.movimiento_padre_id);
    const childrenMap = {};
    for (const m of filtered) {
      if (m.movimiento_padre_id) {
        if (!childrenMap[m.movimiento_padre_id]) childrenMap[m.movimiento_padre_id] = [];
        childrenMap[m.movimiento_padre_id].push(m);
      }
    }
    const result = [];
    for (const parent of parents) {
      result.push(parent);
      if (childrenMap[parent.id]) result.push(...childrenMap[parent.id]);
    }
    // Hijos huérfanos (padre filtrado fuera)
    const parentIds = new Set(parents.map((m) => m.id));
    for (const m of filtered) {
      if (m.movimiento_padre_id && !parentIds.has(m.movimiento_padre_id)) result.push(m);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    movimientos,
    filtroTipo,
    filtroOrigen,
    filtroMoneda,
    filtroFechaDesde,
    filtroFechaHasta,
    normalizedPais,
    mostrarPrevios,
  ]);

  // =====================================================
  // MONEDA PRINCIPAL (para resumen)
  // =====================================================

  const monedaPrincipal = normalizedPais === "Chile" ? "CLP" : "PEN";

  // Convierte un monto a moneda local según su moneda y fecha
  const toMonedaLocal = (monto, monedaMov, fechaMov) => {
    const num = Number(monto) || 0;
    if (monedaMov === "USD") return Math.round(num * getDolarValue(fechaMov, normalizedPais));
    if (monedaMov === "UF") return Math.round(num * getUFValue(fechaMov));
    return num;
  };

  const headerStats = useMemo(() => {
    const isSI = (m) => m.entidad_nombre === "Saldo Inicial";
    const sumRaw = (arr) => arr.reduce((s, m) => s + (Number(m.monto) || 0), 0);
    const byMoneda = (moneda) => {
      const movs = movimientosFiltrados.filter((m) => m.moneda === moneda);
      const si = sumRaw(movs.filter(isSI));
      const cobros = sumRaw(movs.filter((m) => m.tipo === "cobro" && !isSI(m)));
      const pagos = sumRaw(movs.filter((m) => m.tipo === "pago"));
      return { cobros, pagos, saldo: si + cobros - pagos };
    };
    return {
      local: byMoneda(monedaPrincipal),
      usd: byMoneda("USD"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movimientosFiltrados, monedaPrincipal]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const handleOpenNew = (tipo) => {
    setEditingMovimiento(null);
    setDefaultTipo(tipo);
    setShowModal(true);
  };

  const handleEdit = (movimiento) => {
    setEditingMovimiento(movimiento);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMovimiento(null);
  };

  const handleSaveCambio = async ({ fecha, monedaOrigen, montoOrigen, monedaDestino, montoDestino, tipoCambio }) => {
    const base = {
      fecha,
      estado: "confirmado",
      entidad_tipo: "otro",
      entidad_nombre: "Cambio de Moneda",
      pais: normalizedPais,
      registrado_por: id,
    };
    const tcRef = `TC: ${tipoCambio}`;
    await crearMovimientoConHijo(
      { ...base, tipo: "cobro", moneda: monedaDestino, monto: montoDestino, numero_documento: tcRef },
      {
        ...base,
        tipo: "pago",
        moneda: monedaOrigen,
        monto: montoOrigen,
        numero_documento: tcRef,
        categoria_pago: "otro",
      }
    );
    toast.success("Cambio de moneda registrado");
    setShowCambioModal(false);
    await fetchData();
  };

  const handleSave = async (formData) => {
    try {
      if (editingMovimiento) {
        await actualizarMovimiento(editingMovimiento.id, formData);
        toast.success("Movimiento actualizado");
      } else {
        // Limpiar campos que no van a la tabla movimientos_caja
        const {
          vertical_cobro: _vc,
          cobro_factura_id: _cfi,
          facturacion_ref_mes: _frm,
          facturacion_ref_ano: _fra,
          exento_impuesto: _ei,
          cobro_otra_moneda: _com,
          _factoring,
          _costo_transferencia,
          // Campos transitorios del form (no persistir)
          factoring_enabled: _fe,
          factoring_modo: _fm,
          factoring_proveedor_id: _fpi,
          factoring_proveedor_nombre: _fpn,
          factoring_monto_neto: _fmn,
          factoring_porcentaje: _fp,
          factoring_monto_recibido: _fmr,
          costo_transferencia_enabled: _cte,
          costo_transferencia_banco_id: _ctbi,
          costo_transferencia_banco_nombre: _ctbn,
          costo_transferencia_monto: _ctm,
          costo_transferencia_exento: _ctex,
          costo_transferencia_descripcion: _ctd,
          costo_transferencia_moneda: _ctmon,
          facturacion_sub_entries: _fse,
          ...dbFields
        } = formData;

        const parentData = { ...dbFields, pais: normalizedPais, registrado_por: id };

        if (_factoring && _factoring.monto > 0 && _factoring.proveedor_id) {
          // Cobro con factoring → crear padre + hijo
          const childData = {
            tipo: "pago",
            fecha: dbFields.fecha,
            moneda: dbFields.moneda,
            monto: _factoring.monto,
            monto_neto: _factoring.monto_neto,
            monto_impuesto: _factoring.monto_impuesto,
            estado: dbFields.estado,
            entidad_tipo: "proveedor",
            entidad_id: _factoring.proveedor_id,
            entidad_nombre: _factoring.proveedor_nombre,
            categoria_pago: "costo_financiero",
            descripcion: "Costo factoring",
            pais: normalizedPais,
            registrado_por: id,
          };
          try {
            await crearMovimientoConHijo(parentData, childData);
          } catch (childErr) {
            console.error("Error creando hijo factoring:", childErr);
            // Al menos guardar el padre
            await crearMovimiento(parentData);
            toast.error("Cobro registrado, pero error al crear costo factoring: " + (childErr.message || ""));
          }
        } else if (_costo_transferencia && _costo_transferencia.monto > 0 && _costo_transferencia.banco_id) {
          // Pago con costos bancarios asociados → crear padre + hijo
          const childData = {
            tipo: "pago",
            fecha: dbFields.fecha,
            moneda: _costo_transferencia.moneda || dbFields.moneda,
            monto: _costo_transferencia.monto,
            monto_neto: _costo_transferencia.monto_neto,
            monto_impuesto: _costo_transferencia.monto_impuesto,
            estado: dbFields.estado,
            entidad_tipo: "proveedor",
            entidad_id: _costo_transferencia.banco_id,
            entidad_nombre: _costo_transferencia.banco_nombre,
            categoria_pago: "costo_financiero",
            descripcion: _costo_transferencia.descripcion || "Costo bancario asociado",
            pais: normalizedPais,
            registrado_por: id,
          };
          try {
            await crearMovimientoConHijo(parentData, childData);
          } catch (childErr) {
            console.error("Error creando hijo transferencia:", childErr);
            await crearMovimiento(parentData);
            toast.error("Pago registrado, pero error al crear costo transferencia: " + (childErr.message || ""));
          }
        } else {
          await crearMovimiento(parentData);
        }
        // Si vinculó factura, actualizar monto_pagado (con conversión de moneda si aplica)
        if (formData.facturacion_id && formData.monto) {
          const fact = facturas.find((f) => f.id === formData.facturacion_id);
          if (fact) {
            let montoEnMonedaFactura = Number(formData.monto);

            // Convertir si el pago es en moneda distinta a la factura
            if (formData.moneda !== fact.moneda) {
              const monedaLocal = normalizedPais === "Chile" ? "CLP" : "PEN";
              const dolarRate = getDolarValue(formData.fecha, normalizedPais);

              // Paso 1: convertir monto del pago a moneda local
              let enLocal = Number(formData.monto);
              if (formData.moneda === "USD") enLocal = formData.monto * dolarRate;
              else if (formData.moneda === "UF") enLocal = formData.monto * getUFValue(formData.fecha);

              // Paso 2: convertir de moneda local a moneda de la factura
              if (fact.moneda === monedaLocal) {
                montoEnMonedaFactura = enLocal;
              } else if (fact.moneda === "USD") {
                montoEnMonedaFactura = enLocal / dolarRate;
              } else if (fact.moneda === "UF") {
                montoEnMonedaFactura = enLocal / getUFValue(formData.fecha);
              }

              // Redondear a 2 decimales para USD/UF
              montoEnMonedaFactura = Math.round(montoEnMonedaFactura * 100) / 100;
            }

            const nuevoMontoPagado = Number(fact.monto_pagado) + montoEnMonedaFactura;
            const nuevoEstado = nuevoMontoPagado >= Number(fact.monto_total) ? "pagada" : "parcial";
            await actualizarFacturaProveedor(fact.id, { monto_pagado: nuevoMontoPagado, estado: nuevoEstado });
          }
        }
        // ─── Cobro vinculado a facturación: marcar como cobrada si pago total ───
        if (formData.facturacion_tipo && formData.facturacion_ref_id) {
          try {
            await marcarFacturaCobrada(formData);
          } catch (cobErr) {
            console.error("Error actualizando facturación:", cobErr);
            toast.error("Cobro registrado, pero error al actualizar factura origen");
          }
        }

        toast.success("Movimiento registrado");
      }
      handleCloseModal();
      await fetchData();
    } catch (err) {
      console.error("Error guardando movimiento:", err);
      toast.error(err.message || "Error al guardar el movimiento");
    }
  };

  /**
   * Marca la factura de venta como cobrada (facturafechacobro = fecha).
   * Para pagos parciales futuros: se podría comparar total cobrado vs importe.
   * Por ahora marca como cobrada directamente.
   */
  const marcarFacturaCobrada = async (cobrarData) => {
    const {
      facturacion_tipo,
      facturacion_ref_id,
      facturacion_ref_index,
      facturacion_ref_mes,
      facturacion_ref_ano,
      fecha,
    } = cobrarData;
    const cobro = { facturafechacobro: fecha };

    if (facturacion_tipo === "pauta") {
      await updateOportunidad({ id: facturacion_ref_id, oportunidad: cobro });
    } else if (facturacion_tipo === "BTL") {
      await updateOportunidadBTL({ id: facturacion_ref_id, oportunidad: cobro });
    } else if (facturacion_tipo === "Contratos" || facturacion_tipo === "contrato") {
      const { data: contrato } = await client
        .from("sales_contratos")
        .select("meses")
        .eq("id", facturacion_ref_id)
        .single();
      if (contrato && contrato.meses) {
        const updatedMeses = [...contrato.meses];
        if (facturacion_ref_index != null && updatedMeses[facturacion_ref_index]) {
          updatedMeses[facturacion_ref_index] = { ...updatedMeses[facturacion_ref_index], ...cobro };
          await updateContrato({ id: facturacion_ref_id, contrato: { meses: updatedMeses } });
        }
      }
    } else if (facturacion_tipo === "HW-CAPEX") {
      const { data: negocio } = await client
        .from("negocios_carteleria")
        .select("detalle_pagos_hardware")
        .eq("id", facturacion_ref_id)
        .single();
      if (negocio) {
        const detalle =
          typeof negocio.detalle_pagos_hardware === "string"
            ? JSON.parse(negocio.detalle_pagos_hardware || "[]")
            : negocio.detalle_pagos_hardware || [];
        if (facturacion_ref_index != null && detalle[facturacion_ref_index]) {
          detalle[facturacion_ref_index] = { ...detalle[facturacion_ref_index], ...cobro };
          await client
            .from("negocios_carteleria")
            .update({ detalle_pagos_hardware: detalle })
            .eq("id", facturacion_ref_id);
        }
      }
    } else if (facturacion_tipo === "HW-OPEX" || facturacion_tipo === "Gestión") {
      const tipoFact = facturacion_tipo === "HW-OPEX" ? "opex" : "gestion";
      const { data: negocio } = await client
        .from("negocios_carteleria")
        .select("meses_facturacion")
        .eq("id", facturacion_ref_id)
        .single();
      if (negocio) {
        const mesesFact = negocio.meses_facturacion
          ? typeof negocio.meses_facturacion === "string"
            ? JSON.parse(negocio.meses_facturacion)
            : negocio.meses_facturacion
          : [];
        // Buscar el entry por ref_mes + ref_ano + tipo, o por facturanumero
        const idx = mesesFact.findIndex(
          (mf) => mf.mes === facturacion_ref_mes && mf.ano === facturacion_ref_ano && mf.tipo === tipoFact
        );
        if (idx >= 0) {
          mesesFact[idx] = { ...mesesFact[idx], ...cobro };
          await client
            .from("negocios_carteleria")
            .update({ meses_facturacion: mesesFact })
            .eq("id", facturacion_ref_id);
        }
      }
    } else if (facturacion_tipo === "combined") {
      // Factura consolidada: misma factura cubre múltiples conceptos (ej: HW-OPEX + Gestión)
      const { facturacion_sub_entries: subEntries = [] } = cobrarData;
      const { data: negocio } = await client
        .from("negocios_carteleria")
        .select("meses_facturacion, detalle_pagos_hardware")
        .eq("id", facturacion_ref_id)
        .single();
      if (negocio) {
        const mesesFact = negocio.meses_facturacion
          ? typeof negocio.meses_facturacion === "string"
            ? JSON.parse(negocio.meses_facturacion)
            : negocio.meses_facturacion
          : [];
        const detalle = negocio.detalle_pagos_hardware
          ? typeof negocio.detalle_pagos_hardware === "string"
            ? JSON.parse(negocio.detalle_pagos_hardware || "[]")
            : negocio.detalle_pagos_hardware
          : [];
        let updateMeses = false;
        let updateDetalle = false;
        subEntries.forEach((sub) => {
          if (sub.tipo === "HW-CAPEX" && sub.ref_index != null && detalle[sub.ref_index]) {
            detalle[sub.ref_index] = { ...detalle[sub.ref_index], ...cobro };
            updateDetalle = true;
          } else if (sub.tipo === "HW-OPEX" || sub.tipo === "Gestión") {
            const tipoFact = sub.tipo === "HW-OPEX" ? "opex" : "gestion";
            const idx = mesesFact.findIndex(
              (mf) => mf.mes === sub.ref_mes && mf.ano === sub.ref_ano && mf.tipo === tipoFact
            );
            if (idx >= 0) {
              mesesFact[idx] = { ...mesesFact[idx], ...cobro };
              updateMeses = true;
            }
          }
        });
        const updates = {};
        if (updateMeses) updates.meses_facturacion = mesesFact;
        if (updateDetalle) updates.detalle_pagos_hardware = detalle;
        if (Object.keys(updates).length > 0) {
          await client.from("negocios_carteleria").update(updates).eq("id", facturacion_ref_id);
        }
      }
    }
  };

  const handleCrearFactura = async (facturaData, archivo) => {
    try {
      // Separar campos de activo de los campos de factura
      const {
        activo_tipo,
        activo_descripcion,
        activo_numero_serie,
        activo_cantidad,
        activo_destino_tipo,
        activo_destino_id,
        ...facturaFields
      } = facturaData;

      const nueva = await crearFacturaProveedor({ ...facturaFields, pais: normalizedPais });
      if (archivo && nueva) {
        const url = await uploadArchivoFactura(archivo, nueva.id);
        await actualizarFacturaProveedor(nueva.id, { archivo_url: url });
        nueva.archivo_url = url;
      }

      // Si es activo, crear registro en tabla activos
      if (facturaFields.tipo_contable === "activo" && activo_tipo && nueva) {
        const cantidad = parseInt(activo_cantidad) || 1;
        const activoData = {
          fecha_compra: facturaFields.fecha_emision,
          tipo_activo: activo_tipo,
          descripcion: activo_descripcion || facturaFields.descripcion || "",
          numero_serie: activo_numero_serie || null,
          numero_factura: facturaFields.numero_factura,
          cantidad,
          costo_unitario: facturaFields.monto_total / cantidad,
          moneda: facturaFields.moneda,
          vida_util_meses: getVidaUtilPorTipo(activo_tipo),
          valor_residual: 0,
          estado: "activo",
          destino_tipo: activo_destino_tipo || null,
          destino_id: activo_destino_id ? parseInt(activo_destino_id) : null,
          pais: normalizedPais,
          eliminado: false,
          updated_at: new Date().toISOString(),
        };
        const nuevoActivo = await crearActivo({
          ...activoData,
          proveedor_id: facturaFields.proveedor_id || null,
        });
        if (nuevoActivo) {
          await actualizarFacturaProveedor(nueva.id, { activo_id: nuevoActivo.id });
          nueva.activo_id = nuevoActivo.id;
        }
      }

      // Actualizar defaults del proveedor (memoria)
      if (nueva && facturaFields.tipo_contable) {
        const updates = { tipo_contable_default: facturaFields.tipo_contable };
        if (facturaFields.tipo_contable === "gasto" && facturaFields.cuenta_er) {
          updates.cuenta_er_default = facturaFields.cuenta_er;
        }
        await actualizarProveedor(facturaFields.proveedor_id, updates);
        setProveedoresState((prev) =>
          prev.map((p) => (p.id === facturaFields.proveedor_id ? { ...p, ...updates } : p))
        );
      }

      toast.success("Factura registrada");
      setFacturas((prev) => [nueva, ...prev]);
      return nueva;
    } catch (err) {
      console.error("Error creando factura:", err);
      toast.error(`Error al registrar factura: ${err.message || err}`);
      return null;
    }
  };

  const handleSubirArchivoFactura = async (facturaId, file) => {
    try {
      const url = await uploadArchivoFactura(file, facturaId);
      await actualizarFacturaProveedor(facturaId, { archivo_url: url });
      setFacturas((prev) => prev.map((f) => (f.id === facturaId ? { ...f, archivo_url: url } : f)));
      toast.success("Archivo subido");
    } catch (err) {
      console.error("Error subiendo archivo:", err);
      toast.error("Error al subir archivo");
    }
  };

  const handleAnularFactura = async (facturaId) => {
    try {
      // 1. Anular movimientos vinculados a esta factura
      const movsVinculados = movimientos.filter((m) => m.facturacion_id === facturaId && m.estado !== "anulado");
      for (const mov of movsVinculados) {
        // Si era cobro vinculado a facturación de venta, revertir facturafechacobro
        if (mov.facturacion_tipo && mov.facturacion_ref_id) {
          try {
            await revertirFacturaCobrada(mov);
          } catch (revErr) {
            console.error("Error revirtiendo facturafechacobro:", revErr);
          }
        }
        await anularMovimiento(mov.id);
      }

      // 2. Anular la factura
      await actualizarFacturaProveedor(facturaId, { estado: "anulada" });
      setFacturas((prev) => prev.filter((f) => f.id !== facturaId));

      const cantAnulados = movsVinculados.length;
      const msg =
        cantAnulados > 0
          ? `Factura anulada (+ ${cantAnulados} movimiento${cantAnulados > 1 ? "s" : ""} vinculado${cantAnulados > 1 ? "s" : ""})`
          : "Factura anulada";
      toast.success(msg);
      if (cantAnulados > 0) await fetchData();
    } catch (err) {
      console.error("Error anulando factura:", err);
      toast.error(`Error al anular factura: ${err.message || err}`);
    }
  };

  const handleCrearProveedor = async (nombre, extras = {}) => {
    try {
      let nuevo = null;
      try {
        nuevo = await crearProveedor({ nombre, pais: normalizedPais, ...extras });
      } catch (extrasErr) {
        // Si falla con extras (ej: es_factoring no existe aún), reintentar sin extras
        console.warn("Reintentando sin extras:", extrasErr.message);
        nuevo = await crearProveedor({ nombre, pais: normalizedPais });
        if (nuevo && Object.keys(extras).length > 0) {
          // Guardar extras localmente en el objeto para que el filtro funcione en la sesión
          Object.assign(nuevo, extras);
        }
      }
      if (nuevo) {
        toast.success(`Proveedor "${nombre}" creado`);
        setProveedoresState((prev) => [...prev, nuevo].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      }
      return nuevo;
    } catch (err) {
      console.error("Error creando proveedor:", err);
      toast.error("Error al crear proveedor");
      return null;
    }
  };

  const handleAnular = async (movimientoId) => {
    try {
      const mov = movimientos.find((m) => m.id === movimientoId);

      // Si cobro vinculado a facturación, revertir facturafechacobro
      if (mov && mov.facturacion_tipo && mov.facturacion_ref_id) {
        try {
          await revertirFacturaCobrada(mov);
        } catch (revErr) {
          console.error("Error revirtiendo facturafechacobro:", revErr);
        }
      }

      // Si pago vinculado a factura proveedor, revertir monto_pagado
      if (mov && mov.tipo === "pago" && mov.facturacion_id) {
        try {
          const fact = facturas.find((f) => f.id === mov.facturacion_id);
          if (fact) {
            const nuevoMontoPagado = Math.max(0, Number(fact.monto_pagado) - Number(mov.monto));
            const nuevoEstado = nuevoMontoPagado <= 0 ? "pendiente" : "parcial";
            await actualizarFacturaProveedor(fact.id, {
              monto_pagado: nuevoMontoPagado,
              estado: nuevoEstado,
            });
          }
        } catch (revErr) {
          console.error("Error revirtiendo monto_pagado factura:", revErr);
        }
      }

      const { anulados } = await anularMovimientoConHijos(movimientoId);
      const msg =
        anulados > 1
          ? `Movimiento anulado (+ ${anulados - 1} vinculado${anulados > 2 ? "s" : ""})`
          : "Movimiento anulado";
      toast.success(msg);
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      console.error("Error anulando movimiento:", err);
      toast.error("Error al anular el movimiento");
    }
  };

  /**
   * Revierte facturafechacobro a null cuando se anula un cobro vinculado.
   */
  const revertirFacturaCobrada = async (mov) => {
    const { facturacion_tipo, facturacion_ref_id, facturacion_ref_index, numero_documento } = mov;
    const reset = { facturafechacobro: null };

    if (facturacion_tipo === "pauta") {
      await updateOportunidad({ id: facturacion_ref_id, oportunidad: reset });
    } else if (facturacion_tipo === "BTL") {
      await updateOportunidadBTL({ id: facturacion_ref_id, oportunidad: reset });
    } else if (facturacion_tipo === "Contratos" || facturacion_tipo === "contrato") {
      const { data: contrato } = await client
        .from("sales_contratos")
        .select("meses")
        .eq("id", facturacion_ref_id)
        .single();
      if (contrato && contrato.meses && facturacion_ref_index != null) {
        const updatedMeses = [...contrato.meses];
        if (updatedMeses[facturacion_ref_index]) {
          updatedMeses[facturacion_ref_index] = { ...updatedMeses[facturacion_ref_index], ...reset };
          await updateContrato({ id: facturacion_ref_id, contrato: { meses: updatedMeses } });
        }
      }
    } else if (facturacion_tipo === "HW-CAPEX") {
      const { data: negocio } = await client
        .from("negocios_carteleria")
        .select("detalle_pagos_hardware")
        .eq("id", facturacion_ref_id)
        .single();
      if (negocio && facturacion_ref_index != null) {
        const detalle =
          typeof negocio.detalle_pagos_hardware === "string"
            ? JSON.parse(negocio.detalle_pagos_hardware || "[]")
            : negocio.detalle_pagos_hardware || [];
        if (detalle[facturacion_ref_index]) {
          detalle[facturacion_ref_index] = { ...detalle[facturacion_ref_index], ...reset };
          await client
            .from("negocios_carteleria")
            .update({ detalle_pagos_hardware: detalle })
            .eq("id", facturacion_ref_id);
        }
      }
    } else if (facturacion_tipo === "HW-OPEX" || facturacion_tipo === "Gestión") {
      const tipoFact = facturacion_tipo === "HW-OPEX" ? "opex" : "gestion";
      const { data: negocio } = await client
        .from("negocios_carteleria")
        .select("meses_facturacion")
        .eq("id", facturacion_ref_id)
        .single();
      if (negocio) {
        const mesesFact = negocio.meses_facturacion
          ? typeof negocio.meses_facturacion === "string"
            ? JSON.parse(negocio.meses_facturacion)
            : negocio.meses_facturacion
          : [];
        // Buscar por facturanumero del movimiento
        const idx = mesesFact.findIndex((mf) => mf.tipo === tipoFact && mf.facturanumero === numero_documento);
        if (idx >= 0) {
          mesesFact[idx] = { ...mesesFact[idx], ...reset };
          await client
            .from("negocios_carteleria")
            .update({ meses_facturacion: mesesFact })
            .eq("id", facturacion_ref_id);
        }
      }
    } else if (facturacion_tipo === "combined") {
      // Revertir factura consolidada: buscar todas las entries con ese facturanumero
      const { data: negocio } = await client
        .from("negocios_carteleria")
        .select("meses_facturacion, detalle_pagos_hardware")
        .eq("id", facturacion_ref_id)
        .single();
      if (negocio) {
        const mesesFact = negocio.meses_facturacion
          ? typeof negocio.meses_facturacion === "string"
            ? JSON.parse(negocio.meses_facturacion)
            : negocio.meses_facturacion
          : [];
        const detalle = negocio.detalle_pagos_hardware
          ? typeof negocio.detalle_pagos_hardware === "string"
            ? JSON.parse(negocio.detalle_pagos_hardware || "[]")
            : negocio.detalle_pagos_hardware
          : [];
        let updateMeses = false;
        let updateDetalle = false;
        // Revertir todas las entries con el mismo facturanumero
        mesesFact.forEach((mf, idx) => {
          if (mf.facturanumero === numero_documento && mf.facturafechacobro) {
            mesesFact[idx] = { ...mf, ...reset };
            updateMeses = true;
          }
        });
        detalle.forEach((entry, idx) => {
          if (entry.facturanumero === numero_documento && entry.facturafechacobro) {
            detalle[idx] = { ...entry, ...reset };
            updateDetalle = true;
          }
        });
        const updates = {};
        if (updateMeses) updates.meses_facturacion = mesesFact;
        if (updateDetalle) updates.detalle_pagos_hardware = detalle;
        if (Object.keys(updates).length > 0) {
          await client.from("negocios_carteleria").update(updates).eq("id", facturacion_ref_id);
        }
      }
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <div className={styles.empty}>Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={<h2 className={styles.titulo}>Caja</h2>}
        centerContent={
          <div className={styles.headerTableWrap}>
            <table className={styles.headerTable}>
              <thead>
                <tr>
                  <th />
                  <th>Cobros</th>
                  <th>Pagos</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.htMoneda}>{monedaPrincipal}</td>
                  <td className={styles.htCobros}>
                    {monedaSimbolo(monedaPrincipal)} {formatNum(headerStats.local.cobros)}
                  </td>
                  <td className={styles.htPagos}>
                    {monedaSimbolo(monedaPrincipal)} {formatNum(headerStats.local.pagos)}
                  </td>
                  <td
                    className={styles.htSaldo}
                    style={{ color: headerStats.local.saldo >= 0 ? "#155724" : "#721c24" }}
                  >
                    {monedaSimbolo(monedaPrincipal)} {formatNum(headerStats.local.saldo)}
                  </td>
                </tr>
                <tr>
                  <td className={styles.htMoneda}>USD</td>
                  <td className={styles.htCobros}>US$ {formatNum(headerStats.usd.cobros)}</td>
                  <td className={styles.htPagos}>US$ {formatNum(headerStats.usd.pagos)}</td>
                  <td className={styles.htSaldo} style={{ color: headerStats.usd.saldo >= 0 ? "#155724" : "#721c24" }}>
                    US$ {formatNum(headerStats.usd.saldo)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        }
      >
        {canEdit && (
          <div style={{ display: "flex", flexDirection: "row", gap: 6 }}>
            <button
              className={`btn ${styles.btnNuevoCobro} ${styles.btnNuevaFactura}`}
              onClick={() => {
                setActiveTab("facturas");
                setAutoOpenFacturaModal(true);
              }}
            >
              + Factura
            </button>
            <button className={`btn ${styles.btnCambio}`} onClick={() => setShowCambioModal(true)}>
              Cambio
            </button>
            <button className={`btn ${styles.btnNuevoCobro}`} onClick={() => handleOpenNew("cobro")}>
              Cobro
            </button>
            <button className={`btn ${styles.btnNuevoPago}`} onClick={() => handleOpenNew("pago")}>
              Pago
            </button>
          </div>
        )}
      </Header>

      <div className={styles.content}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "movimientos" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("movimientos")}
          >
            Movimientos
          </button>
          <button
            className={`${styles.tab} ${activeTab === "facturas" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("facturas")}
          >
            Proveedores
          </button>
          <button
            className={`${styles.tab} ${activeTab === "estadoCuenta" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("estadoCuenta")}
          >
            Estado de Cuenta
          </button>
        </div>

        {/* Banner pendientes bancarios */}
        {pendientesBancarios > 0 && (
          <Link
            to="/admin/movimientos-bancarios"
            style={{
              display: "block",
              textAlign: "center",
              padding: "8px 16px",
              marginBottom: 12,
              background: "#ffecd2",
              borderRadius: 6,
              color: "#e67e22",
              fontSize: "0.82rem",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {pendientesBancarios} movimiento{pendientesBancarios === 1 ? "" : "s"} bancario
            {pendientesBancarios === 1 ? "" : "s"} pendiente{pendientesBancarios === 1 ? "" : "s"} de clasificar →
          </Link>
        )}

        {/* Tab: Movimientos */}
        {activeTab === "movimientos" && (
          <>
            {/* Filtros */}
            <div className={styles.filterBar}>
              <select className="form-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="">Tipo</option>
                <option value="cobro">Cobros</option>
                <option value="pago">Pagos</option>
              </select>
              <select className="form-select" value={filtroMoneda} onChange={(e) => setFiltroMoneda(e.target.value)}>
                <option value="">Moneda</option>
                <option value="local">{normalizedPais === "Chile" ? "CLP" : "PEN"}</option>
                <option value="USD">USD</option>
              </select>
              <select className="form-select" value={filtroOrigen} onChange={(e) => setFiltroOrigen(e.target.value)}>
                <option value="">Origen</option>
                <option value="clay">Clay</option>
                <option value="manual">Manual</option>
              </select>
              {!mostrarPrevios && (
                <>
                  <input
                    type="date"
                    className="form-control"
                    value={filtroFechaDesde}
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={filtroFechaHasta}
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                  />
                </>
              )}
              <label className={styles.filterCheckbox}>
                <input
                  type="checkbox"
                  checked={mostrarDecimales}
                  onChange={(e) => setMostrarDecimales(e.target.checked)}
                />
                Decimales
              </label>
              <label className={styles.filterCheckbox}>
                <input type="checkbox" checked={mostrarPrevios} onChange={(e) => setMostrarPrevios(e.target.checked)} />
                Ver movimientos previos
              </label>
            </div>

            {/* Tabla movimientos */}
            {movimientosFiltrados.length === 0 ? (
              <div className={styles.empty}>No se encontraron movimientos para {normalizedPais}</div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.movimientosTable}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Contraparte</th>
                      <th>Categoría</th>
                      <th>Documento</th>
                      <th>Neto</th>
                      <th>{normalizedPais === "Chile" ? "IVA" : "IGV"}</th>
                      <th>Total</th>
                      <th>Estado</th>
                      {canEdit && <th>Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const parentIdsWithChildren = new Set(
                        movimientosFiltrados.filter((m) => m.movimiento_padre_id).map((m) => m.movimiento_padre_id)
                      );
                      return movimientosFiltrados.map((mov) => {
                        const isChild = !!mov.movimiento_padre_id;
                        const isParentWithChildren = parentIdsWithChildren.has(mov.id);
                        const bgClass =
                          mov.entidad_nombre === "Saldo Inicial"
                            ? styles.saldoInicialRow
                            : mov.tipo === "cobro"
                              ? styles.cobroRow
                              : styles.pagoRow;
                        const linkedClass = isChild
                          ? styles.linkedChildRow
                          : isParentWithChildren
                            ? styles.linkedParentRow
                            : "";
                        return (
                          <tr key={mov.id} className={`${bgClass} ${linkedClass}`}>
                            <td>
                              {isChild && <span className={styles.childConnector}>↳</span>}
                              {formatFechaDisplay(mov.fecha)}
                              {mov.origen === "clay" && (
                                <span
                                  title="Importado desde Clay"
                                  style={{
                                    display: "inline-block",
                                    marginLeft: 4,
                                    fontSize: "0.55rem",
                                    background: "#e8f4fd",
                                    color: "#2980b9",
                                    padding: "0 4px",
                                    borderRadius: 3,
                                    fontWeight: 700,
                                    verticalAlign: "middle",
                                  }}
                                >
                                  C
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`${styles.tipoBadge} ${mov.entidad_nombre === "Saldo Inicial" ? styles.saldoInicial : styles[mov.tipo]}`}
                              >
                                {mov.entidad_nombre === "Saldo Inicial"
                                  ? "Saldo"
                                  : mov.tipo === "cobro"
                                    ? "Cobro"
                                    : "Pago"}
                              </span>
                            </td>
                            <td>
                              {mov.entidad_nombre ||
                                (() => {
                                  if (!mov.entidad_id) return "-";
                                  if (mov.entidad_tipo === "empleado") {
                                    const emp = empleados.find((e) => e.id === mov.entidad_id);
                                    return emp ? `${emp.nombre} ${emp.apellido}` : "-";
                                  }
                                  if (mov.entidad_tipo === "proveedor") {
                                    const prov = proveedores.find((p) => p.id === mov.entidad_id);
                                    return prov ? prov.nombre : "-";
                                  }
                                  if (mov.entidad_tipo === "edificio") {
                                    const ed = edificios.find((e) => e.id === mov.entidad_id);
                                    return ed ? ed.edificio : "-";
                                  }
                                  return "-";
                                })()}
                            </td>
                            <td>
                              {mov.entidad_nombre === "Saldo Inicial"
                                ? "-"
                                : mov.entidad_nombre === "Cambio de Moneda"
                                  ? mov.tipo === "cobro"
                                    ? "Compra de Moneda"
                                    : "Venta de Moneda"
                                  : mov.tipo === "cobro"
                                    ? getCategoriaCobroLabel(mov.facturacion_tipo)
                                    : (() => {
                                        if (mov.facturacion_id) {
                                          const fv = facturas.find((f) => f.id === mov.facturacion_id);
                                          if (fv?.cuenta_er) return CUENTA_ER_LABEL[fv.cuenta_er] || fv.cuenta_er;
                                        }
                                        return CATEGORIAS_LABEL[mov.categoria_pago] || mov.categoria_pago || "-";
                                      })()}
                            </td>
                            <td>{mov.numero_documento || "-"}</td>
                            <td>
                              {mov.entidad_nombre === "Saldo Inicial"
                                ? "-"
                                : mov.monto_neto
                                  ? `${monedaSimbolo(mov.moneda)} ${formatNum(mov.monto_neto)}`
                                  : `${monedaSimbolo(mov.moneda)} ${formatNum(mov.monto)}`}
                            </td>
                            <td>
                              {mov.monto_impuesto
                                ? `${monedaSimbolo(mov.moneda)} ${formatNum(mov.monto_impuesto)}`
                                : "-"}
                            </td>
                            <td
                              style={{
                                fontWeight: 600,
                                color: mov.tipo === "cobro" ? "#27ae60" : mov.tipo === "pago" ? "#c0392b" : "#333",
                              }}
                            >
                              {monedaSimbolo(mov.moneda)} {formatNum(mov.monto)}
                            </td>
                            <td>
                              <span className={`${styles.estadoBadge} ${styles[mov.estado]}`}>
                                {mov.estado === "confirmado"
                                  ? "Confirmado"
                                  : mov.estado === "proyectado"
                                    ? "Proyección"
                                    : "Pendiente"}
                              </span>
                            </td>
                            {canEdit && (
                              <td>
                                {confirmDelete === mov.id ? (
                                  <>
                                    <button
                                      className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                      onClick={() => handleAnular(mov.id)}
                                      title="Confirmar anulación"
                                      style={{ color: "#c62828", fontWeight: 600, fontSize: "0.7rem" }}
                                    >
                                      Sí
                                    </button>
                                    <button
                                      className={`${styles.actionBtn}`}
                                      onClick={() => setConfirmDelete(null)}
                                      title="Cancelar"
                                      style={{ fontSize: "0.7rem" }}
                                    >
                                      No
                                    </button>
                                  </>
                                ) : isChild ? (
                                  <button
                                    className={`${styles.actionBtn} ${styles.editBtn}`}
                                    onClick={() => handleEdit(mov)}
                                    title="Editar vinculado"
                                  >
                                    <FaEdit />
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      className={`${styles.actionBtn} ${styles.editBtn}`}
                                      onClick={() => handleEdit(mov)}
                                      title="Editar"
                                    >
                                      <FaEdit />
                                    </button>
                                    <button
                                      className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                      onClick={() => setConfirmDelete(mov.id)}
                                      title="Anular"
                                    >
                                      <FaTrash />
                                    </button>
                                  </>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Tab: Facturas Proveedor */}
        {activeTab === "facturas" && (
          <FacturasProveedorTab
            facturas={facturas}
            proveedores={proveedores}
            normalizedPais={normalizedPais}
            formatAmount={formatAmount}
            onCrearFactura={handleCrearFactura}
            onCrearProveedor={handleCrearProveedor}
            onSubirArchivo={handleSubirArchivoFactura}
            onAnularFactura={handleAnularFactura}
            edificios={edificios}
            proyectosCarteleria={proyectosCarteleria}
            canEdit={canEdit}
            autoOpenModal={autoOpenFacturaModal}
            onAutoOpenModalHandled={() => setAutoOpenFacturaModal(false)}
            siiRecibidasVinculadas={siiRecibidasVinculadas}
          />
        )}

        {/* Tab: Estado de Cuenta */}
        {activeTab === "estadoCuenta" && (
          <EstadoCuentaTab
            movimientos={movimientos}
            facturas={facturas}
            proveedores={proveedores}
            empleados={empleados}
            edificios={edificios}
            formatAmount={formatAmount}
            toMonedaLocal={toMonedaLocal}
            monedaPrincipal={monedaPrincipal}
            normalizedPais={normalizedPais}
          />
        )}
      </div>

      {/* Modal cambio de moneda */}
      <CambioMonedaModal
        show={showCambioModal}
        onClose={() => setShowCambioModal(false)}
        onSave={handleSaveCambio}
        normalizedPais={normalizedPais}
      />

      {/* Modal */}
      <MovimientoModal
        show={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        onCrearProveedor={handleCrearProveedor}
        onCrearFactura={handleCrearFactura}
        editingMovimiento={editingMovimiento}
        defaultTipo={defaultTipo}
        normalizedPais={normalizedPais}
        clientes={clientes}
        proveedores={proveedores}
        empleados={empleados}
        edificios={edificios}
        facturas={facturas}
        facturasPendientesCobro={pendientesCobro}
      />
    </div>
  );
};

export default FlujoCaja;
